import React, { useEffect, useRef } from "react";
import {
  APIProvider,
  Map,
  Marker,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { GOOGLE_MAPS_API_KEY, MAP_ID } from "../../config";
import { useMapColorScheme } from "../../hooks/useMapColorScheme";

// Draws the geofence circle and fits the map to it. Imperative because
// @vis.gl/react-google-maps doesn't ship a Circle component.
function PreviewCircle({ center, radius }) {
  const map = useMap();
  const mapsLib = useMapsLibrary("maps");
  const circleRef = useRef(null);

  useEffect(() => {
    if (!map || !mapsLib || !center) return;
    if (!circleRef.current) {
      circleRef.current = new mapsLib.Circle({
        map,
        strokeColor: "#2563eb",
        strokeOpacity: 0.8,
        strokeWeight: 1,
        fillColor: "#2563eb",
        fillOpacity: 0.15,
        clickable: false,
      });
    }
    circleRef.current.setCenter(center);
    circleRef.current.setRadius(radius);
    const bounds = circleRef.current.getBounds();
    if (bounds) map.fitBounds(bounds, 24);
  }, [map, mapsLib, center, radius]);

  useEffect(
    () => () => {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    },
    []
  );

  return null;
}

// Read-only map showing a site's clock-in geofence (pin + circle), fitted to
// the radius. Used to confirm a fence where it can't be edited (e.g. the shift
// form) — editing happens on the site itself.
function GeofencePreviewMap({ latitude, longitude, radius }) {
  const colorScheme = useMapColorScheme();
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const center = { lat, lng };
  const r = Number(radius) > 0 ? Number(radius) : 150;

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
      <div className="rounded-xl overflow-hidden">
        <Map
          key={colorScheme}
          defaultCenter={center}
          defaultZoom={15}
          style={{ width: "100%", height: "180px" }}
          mapId={MAP_ID}
          colorScheme={colorScheme}
          options={{ disableDefaultUI: true, gestureHandling: "cooperative" }}
        >
          <Marker position={center} />
          <PreviewCircle center={center} radius={r} />
        </Map>
      </div>
    </APIProvider>
  );
}

export { GeofencePreviewMap };
